package er.prateek.crossly.ui.screens.crossword

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableLongStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.backhandler.BackHandler
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.key.utf16CodePoint
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.LifecycleEventObserver
import androidx.lifecycle.compose.LocalLifecycleOwner
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.coach_resume_kicker
import crossly.shared.generated.resources.coach_resume_play_body
import crossly.shared.generated.resources.coach_resume_play_title
import crossly.shared.generated.resources.crossword_check
import crossly.shared.generated.resources.crossword_countdown_go
import crossly.shared.generated.resources.crossword_daily_badge
import crossly.shared.generated.resources.crossword_hints_left
// import crossly.shared.generated.resources.crossword_progress
import crossly.shared.generated.resources.crossword_reveal_ad_caption
import crossly.shared.generated.resources.crossword_reveal_ad_loading
import crossly.shared.generated.resources.crossword_reveal_cell_count
import crossly.shared.generated.resources.crossword_reveal_cell_count_short
import crossly.shared.generated.resources.crossword_reveal_grid
import crossly.shared.generated.resources.crossword_reveal_grid_short
import crossly.shared.generated.resources.crossword_no_stars
import crossly.shared.generated.resources.home_daily
import crossly.shared.generated.resources.save_progress_body
import crossly.shared.generated.resources.save_progress_discard
import crossly.shared.generated.resources.save_progress_save
import crossly.shared.generated.resources.save_progress_title
import er.prateek.crossly.ads.AdPlacement
import er.prateek.crossly.config.AppConfig
import er.prateek.crossly.config.RevealLetterConfig
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.Puzzle
import er.prateek.crossly.data.model.SavedPuzzleSession
import er.prateek.crossly.data.model.GameMode
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.firebase.RemoteConfigKeys
import er.prateek.crossly.firebase.hintsMaxPerPuzzle
import er.prateek.crossly.game.crossword.CellStatus
import er.prateek.crossly.game.crossword.CrosswordEngine
import er.prateek.crossly.game.crossword.KeyboardMode
import er.prateek.crossly.game.crossword.LetterSupply
import er.prateek.crossly.game.crossword.RackTile
import er.prateek.crossly.game.crossword.StarterLetters
import er.prateek.crossly.progression.ScoringPolicy
import er.prateek.crossly.progression.recordCompletionAndLog
import er.prateek.crossly.share.formatClock
import er.prateek.crossly.share.puzzleLevelLabel
import er.prateek.crossly.share.tierDisplayName
import er.prateek.crossly.ui.gameplay.GameplayState
import er.prateek.crossly.ui.components.GameButton
import er.prateek.crossly.ui.components.GameButtonSize
import er.prateek.crossly.ui.components.GameButtonStyle
import er.prateek.crossly.ui.components.GameScreen
import er.prateek.crossly.ui.components.GameTopBar
import er.prateek.crossly.ui.components.IconBulb
import er.prateek.crossly.ui.components.IconCheck
import er.prateek.crossly.ui.components.IconEye
import er.prateek.crossly.ui.components.IconStar
import er.prateek.crossly.ui.components.LoadingState
import er.prateek.crossly.ui.components.StatChip
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.window
import er.prateek.crossly.ui.coach.CoachOverlay
import er.prateek.crossly.ui.coach.reportCoachBounds
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import kotlin.time.TimeSource

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun CrosswordScreen(
    puzzleId: String,
    gameplayState: GameplayState,
    onBack: () -> Unit,
    onSolved: (puzzleId: String, stars: Int, elapsedMs: Long) -> Unit,
    isDaily: Boolean = false,
    fromChallenge: Boolean = false,
    challengerName: String = "",
    challengerTimeMs: Long = 0,
) {
    var puzzle by remember(puzzleId) { mutableStateOf<Puzzle?>(null) }

    LaunchedEffect(puzzleId) {
        puzzle = AppContainer.contentRepository.puzzleById(puzzleId)
    }

    val currentPuzzle = puzzle
    if (currentPuzzle == null) {
        GameScreen { LoadingState(modifier = Modifier.fillMaxSize()) }
        return
    }

    // Starter letters are baked into the engine, so changing the setting mid-play
    // restarts the board rather than half-applying to a grid already in progress.
    // Both assists are per level, so the puzzle's own tier picks which choice applies.
    val starterAmount = gameplayState.starterAmount(currentPuzzle.difficulty)
    val restoredSession = remember(puzzleId) {
        AppContainer.savedSessionManager.sessionFor(puzzleId)
    }
    val engine = remember(currentPuzzle, starterAmount) {
        CrosswordEngine(currentPuzzle, StarterLetters.pick(currentPuzzle, starterAmount)).also { created ->
            restoredSession?.let { saved ->
                created.applySnapshot(
                    fills = saved.fills,
                    hintsUsed = saved.hintsUsed,
                    lettersRevealed = saved.lettersRevealed,
                    mistakes = saved.mistakes,
                    selectedRow = saved.selectedRow,
                    selectedCol = saved.selectedCol,
                    directionName = saved.direction,
                )
            }
        }
    }
    val focusRequester = remember(currentPuzzle) { FocusRequester() }
    // Remote Config hands back a Long; the UI counts hints in Int.
    val hintsMax = remember { AppContainer.remoteConfig.hintsMaxPerPuzzle().toInt() }

    // Reveal Letter has its own budget, separate from the legacy hintsMax above (which still
    // gates Reveal Puzzle): a daily cap shared across every puzzle, plus a per-puzzle cap that
    // tightens on easier tiers. Re-read once per puzzle so a Remote Config change lands on the
    // next puzzle open rather than needing an app restart.
    val revealLetterConfig = remember(currentPuzzle) {
        AppConfig.parse(AppContainer.remoteConfig.getString(RemoteConfigKeys.APP_CONFIG)).gameplay.revealLetter
    }
    var dailyRevealsRemaining by remember(currentPuzzle) {
        mutableStateOf(AppContainer.revealLimiter.revealsRemainingToday(revealLetterConfig))
    }

    val showPlayCoach = !AppContainer.coachPrefsStore.hasSeenPlayCoach()
    val showResumeCoach = restoredSession != null &&
        !showPlayCoach &&
        !AppContainer.coachPrefsStore.hasSeenResumeCoach()
    // Locked until the 3-2-1 finishes (or a resume welcome closes) so the clock
    // can't tick during a coach or in the gap before the first countdown beat.
    var startLocked by remember(currentPuzzle) {
        mutableStateOf(restoredSession == null || showPlayCoach || showResumeCoach)
    }
    var countingDown by remember(currentPuzzle) {
        mutableStateOf(restoredSession == null && !showPlayCoach)
    }
    var countdownBeat by remember(currentPuzzle) { mutableStateOf<String?>(null) }
    var coachVisible by remember(currentPuzzle) { mutableStateOf(showPlayCoach) }
    var resumeCoachVisible by remember(currentPuzzle) { mutableStateOf(showResumeCoach) }
    var coachStep by remember(currentPuzzle) { mutableStateOf(PlayCoachStep.HINT) }
    val coachAnchors = remember(currentPuzzle) { CoachMarkAnchors() }
    var selectedCellBounds by remember(currentPuzzle) { mutableStateOf(Rect.Zero) }
    var elapsedMs by remember(currentPuzzle) { mutableLongStateOf(restoredSession?.elapsedMs ?: 0L) }
    var timerPaused by remember { mutableStateOf(false) }
    var leavePrompt by remember { mutableStateOf(false) }
    var puzzleLabel by remember(puzzleId) { mutableStateOf(currentPuzzle.title) }
    val startNowLabel = stringResource(Res.string.crossword_countdown_go)
    val lifecycleOwner = LocalLifecycleOwner.current

    LaunchedEffect(currentPuzzle) {
        if (isDaily) {
            puzzleLabel = currentPuzzle.title
            return@LaunchedEffect
        }
        val inTier = AppContainer.contentRepository.puzzlesByTier(currentPuzzle.difficulty)
        puzzleLabel = puzzleLevelLabel(
            tierDisplayName(currentPuzzle.difficulty),
            inTier.indexOfFirst { it.id == currentPuzzle.id },
        )
    }

    DisposableEffect(lifecycleOwner) {
        val observer = LifecycleEventObserver { _, event ->
            when (event) {
                Lifecycle.Event.ON_PAUSE, Lifecycle.Event.ON_STOP -> timerPaused = true
                Lifecycle.Event.ON_RESUME -> timerPaused = false
                else -> Unit
            }
        }
        lifecycleOwner.lifecycle.addObserver(observer)
        onDispose { lifecycleOwner.lifecycle.removeObserver(observer) }
    }

    LaunchedEffect(coachVisible, resumeCoachVisible, currentPuzzle) {
        if (coachVisible) {
            AppContainer.analytics.logEvent(
                AnalyticsEvents.PLAY_COACH_SHOWN,
                mapOf("puzzle_id" to puzzleId, "is_daily" to isDaily, "resumed" to (restoredSession != null)),
            )
        } else if (resumeCoachVisible) {
            AppContainer.analytics.logEvent(
                AnalyticsEvents.RESUME_COACH_SHOWN,
                mapOf("puzzle_id" to puzzleId, "is_daily" to isDaily, "surface" to "play"),
            )
        }
    }

    // Coach first (when needed), then the countdown. Restarts when the tour
    // closes so "Got it" / Skip hand straight off to 3-2-1. A restored session
    // skips the countdown and waits out any welcome-back coach instead.
    LaunchedEffect(currentPuzzle, startNowLabel, coachVisible, resumeCoachVisible) {
        if (restoredSession != null) {
            if (coachVisible || resumeCoachVisible) return@LaunchedEffect
            countdownBeat = null
            countingDown = false
            startLocked = false
            focusRequester.requestFocus()
            AppContainer.analytics.logEvent(
                AnalyticsEvents.SESSION_RESUMED,
                mapOf(
                    "puzzle_id" to puzzleId,
                    "is_daily" to isDaily,
                    "percent_complete" to restoredSession.percentComplete,
                    "elapsed_ms" to restoredSession.elapsedMs,
                    "hints_used" to restoredSession.hintsUsed,
                    "mistakes" to restoredSession.mistakes,
                    "from_challenge" to fromChallenge,
                ),
            )
            return@LaunchedEffect
        }
        if (coachVisible) return@LaunchedEffect
        countingDown = true
        elapsedMs = 0L
        listOf("3", "2", "1", startNowLabel).forEach { beat ->
            countdownBeat = beat
            delay(if (beat == startNowLabel) 850 else 720)
        }
        countdownBeat = null
        countingDown = false
        startLocked = false
        focusRequester.requestFocus()
        AppContainer.analytics.logEvent(
            if (isDaily) AnalyticsEvents.DAILY_PUZZLE_STARTED else AnalyticsEvents.PUZZLE_STARTED,
            mapOf(
                "puzzle_id" to puzzleId,
                "tier" to currentPuzzle.difficulty.name,
                "from_challenge" to fromChallenge,
                "starter_amount" to starterAmount.name,
                "keyboard_mode" to gameplayState.keyboardMode(currentPuzzle.difficulty).name,
                "total_cells" to engine.fillProgress().second,
            ),
        )
    }

    LaunchedEffect(countingDown, timerPaused, currentPuzzle, engine.isSolved, coachVisible, resumeCoachVisible, startLocked) {
        if (countingDown || timerPaused || engine.isSolved || coachVisible || resumeCoachVisible || startLocked) return@LaunchedEffect
        val started = TimeSource.Monotonic.markNow()
        val base = elapsedMs
        while (true) {
            elapsedMs = base + started.elapsedNow().inWholeMilliseconds
            delay(200)
        }
    }

    // Fires on every leave (back button, solved, or process death via composition
    // teardown) unless already solved -- an abandoned-then-resumed puzzle logs
    // this once per attempt, matching the event's "did the player bail" intent.
    DisposableEffect(currentPuzzle) {
        onDispose {
            if (!engine.isSolved) {
                val (filledOnExit, totalOnExit) = engine.fillProgress()
                AppContainer.analytics.logEvent(
                    AnalyticsEvents.PUZZLE_ABANDONED,
                    mapOf(
                        "puzzle_id" to puzzleId,
                        "is_daily" to isDaily,
                        "tier" to currentPuzzle.difficulty.name,
                        "elapsed_ms" to elapsedMs,
                        "filled_cells" to filledOnExit,
                        "total_cells" to totalOnExit,
                        "percent_complete" to if (totalOnExit > 0) filledOnExit * 100 / totalOnExit else 0,
                        "hints_used" to engine.hintsUsed,
                        "mistakes" to engine.mistakes,
                        "during_countdown" to countingDown,
                        "from_challenge" to fromChallenge,
                    ),
                )
            }
        }
    }

    LaunchedEffect(engine.isSolved) {
        if (engine.isSolved) {
            // Daily play never records into ProgressionManager: the puzzle is
            // drawn from the bundled EASY pool, and level unlocks must stay
            // independent of the daily streak in both directions.
            val stars = if (isDaily) {
                AppContainer.dailyPuzzleManager.recordCompletionAndLog(AppContainer.analytics, GameMode.CROSSWORD)
                ScoringPolicy.starsFor(hintsUsed = engine.hintsUsed, mistakes = engine.mistakes)
            } else {
                val hadStarsBefore = AppContainer.progressionManager.starsFor(puzzleId) > 0
                val puzzlesByTier = DifficultyTier.entries.associateWith { AppContainer.contentRepository.puzzlesByTier(it) }
                val nextTier = DifficultyTier.entries.getOrNull(currentPuzzle.difficulty.ordinal + 1)
                val tierUnlockedBefore = nextTier?.let { AppContainer.progressionManager.isTierUnlocked(it, puzzlesByTier) } ?: false

                val earned = AppContainer.progressionManager.recordCompletion(
                    puzzleId = puzzleId,
                    hintsUsed = engine.hintsUsed,
                    mistakes = engine.mistakes,
                    elapsedMs = elapsedMs,
                )

                if (!hadStarsBefore && earned >= 1) {
                    AppContainer.analytics.logEvent(
                        AnalyticsEvents.LEVEL_UNLOCKED,
                        mapOf("puzzle_id" to puzzleId, "tier" to currentPuzzle.difficulty.name, "stars_earned" to earned),
                    )
                }
                if (nextTier != null && !tierUnlockedBefore &&
                    AppContainer.progressionManager.isTierUnlocked(nextTier, puzzlesByTier)
                ) {
                    AppContainer.analytics.logEvent(
                        AnalyticsEvents.TIER_UNLOCKED,
                        mapOf("tier" to nextTier.name, "unlocked_from_tier" to currentPuzzle.difficulty.name),
                    )
                }
                earned
            }
            val wonChallengeResult = fromChallenge && challengerTimeMs > 0 && elapsedMs < challengerTimeMs
            AppContainer.analytics.logEvent(
                if (isDaily) AnalyticsEvents.DAILY_PUZZLE_COMPLETED else AnalyticsEvents.PUZZLE_COMPLETED,
                mapOf(
                    "puzzle_id" to puzzleId,
                    "tier" to currentPuzzle.difficulty.name,
                    "stars" to stars,
                    "hints_used" to engine.hintsUsed,
                    "letters_revealed" to engine.lettersRevealed,
                    "mistakes" to engine.mistakes,
                    "duration_ms" to elapsedMs,
                    "starter_amount" to starterAmount.name,
                    "keyboard_mode" to gameplayState.keyboardMode(currentPuzzle.difficulty).name,
                    "from_challenge" to fromChallenge,
                    "challenge_won" to wonChallengeResult,
                    "was_resumed" to (restoredSession != null),
                ),
            )
            AppContainer.savedSessionManager.clearIfPuzzle(puzzleId)
            AppContainer.playerJourney.onGameCompleted(GameMode.CROSSWORD, isDaily = isDaily)
            onSolved(puzzleId, stars, elapsedMs)
        }
    }

    val finishPlayCoach: (skipped: Boolean) -> Unit = {
        AppContainer.coachPrefsStore.markPlayCoachSeen()
        if (restoredSession != null) {
            AppContainer.coachPrefsStore.markResumeCoachSeen()
        }
        AppContainer.analytics.logEvent(
            AnalyticsEvents.PLAY_COACH_FINISHED,
            mapOf(
                "skipped" to it,
                "step" to coachStep.name,
                "puzzle_id" to puzzleId,
            ),
        )
        coachVisible = false
    }

    val finishResumeCoach: (skipped: Boolean) -> Unit = {
        AppContainer.coachPrefsStore.markResumeCoachSeen()
        AppContainer.analytics.logEvent(
            AnalyticsEvents.RESUME_COACH_FINISHED,
            mapOf(
                "skipped" to it,
                "puzzle_id" to puzzleId,
                "surface" to "play",
            ),
        )
        resumeCoachVisible = false
    }

    val leaveSolving: () -> Unit = {
        if (engine.isSolved || countingDown || startLocked) {
            onBack()
        } else if (!engine.hasPlayerInput()) {
            // Nothing typed, revealed or hinted: there's no progress to offer to save. Also drop any
            // earlier save of this puzzle -- the player has since cleared it back to empty.
            AppContainer.savedSessionManager.clearIfPuzzle(puzzleId)
            onBack()
        } else {
            leavePrompt = true
            val (filledLeave, totalLeave) = engine.fillProgress()
            AppContainer.analytics.logEvent(
                AnalyticsEvents.LEAVE_PROMPT_SHOWN,
                mapOf(
                    "puzzle_id" to puzzleId,
                    "is_daily" to isDaily,
                    "percent_complete" to if (totalLeave > 0) filledLeave * 100 / totalLeave else 0,
                    "elapsed_ms" to elapsedMs,
                ),
            )
        }
    }
    BackHandler(enabled = !engine.isSolved) {
        when {
            leavePrompt -> leavePrompt = false
            coachVisible -> finishPlayCoach(true)
            resumeCoachVisible -> finishResumeCoach(true)
            else -> leaveSolving()
        }
    }

    val window = MaterialTheme.window
    val colors = MaterialTheme.game
    // Filled-cell progress (e.g. 4/9) is kept off the HUD; still used for analytics elsewhere.
    // val (filled, totalCells) = engine.fillProgress()
    val hintsLeft = (hintsMax - engine.hintsUsed).coerceAtLeast(0)
    val hintsExhausted = hintsLeft <= 0
    val wordCells = engine.currentWordCells()

    // Bumped every time a letter reveal spends a star, so the star chip above can
    // play a little "just spent" animation on top of the count simply changing.
    var starPulse by remember(currentPuzzle) { mutableStateOf(0) }

    val keyboardMode = gameplayState.keyboardMode(currentPuzzle.difficulty)
    val gutter = if (gameplayState.showClueGutter.value) {
        remember(engine) { buildClueGutter(engine) }
    } else {
        null
    }
    // Shared by the board and the keys: letters are dragged from one to the
    // other, and tapped letters fly back the same way.
    val dragState = rememberLetterDragState()

    LaunchedEffect(coachVisible, resumeCoachVisible, engine.selected, dragState) {
        if (!coachVisible && !resumeCoachVisible) {
            selectedCellBounds = Rect.Zero
            return@LaunchedEffect
        }
        while (true) {
            selectedCellBounds = engine.selected?.let { dragState.boundsOf(it) } ?: Rect.Zero
            delay(48)
        }
    }

    // With a word-scoped rack, finishing a word empties the rack — so the cursor
    // moves itself to the next unsolved clue rather than stranding the player in
    // front of a board they can't type into.
    val currentClue = engine.currentClue()
    val currentClueSolved = currentClue != null && engine.isClueSolved(currentClue, engine.direction)
    LaunchedEffect(keyboardMode, currentClue, currentClueSolved) {
        if (keyboardMode == KeyboardMode.WORD && currentClueSolved && !engine.isSolved) {
            delay(300)
            engine.selectNextUnsolvedClue()
        }
    }

    GameScreen(
        adPlacement = AdPlacement.CROSSWORD,
        overlay = {
            countdownBeat?.let { CountdownOverlay(beat = it) }
            if (coachVisible) {
                PlayCoachOverlay(
                    step = coachStep,
                    anchors = coachAnchors,
                    selectedCell = selectedCellBounds,
                    onNext = {
                        val next = coachStep.next()
                        if (next == null) finishPlayCoach(false) else coachStep = next
                    },
                    onSkip = { finishPlayCoach(true) },
                )
            } else if (resumeCoachVisible) {
                CoachOverlay(
                    stepIndex = 0,
                    stepCount = 1,
                    title = stringResource(Res.string.coach_resume_play_title),
                    body = stringResource(Res.string.coach_resume_play_body),
                    hole = selectedCellBounds.takeUnless { it.isEmpty } ?: coachAnchors.board,
                    kicker = stringResource(Res.string.coach_resume_kicker),
                    holeCornerRadius = 12.dp,
                    onNext = { finishResumeCoach(false) },
                    onSkip = { finishResumeCoach(true) },
                )
            }
        },
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .focusRequester(focusRequester)
                .focusable()
                // Desktop and web get full keyboard play: letters and backspace
                // edit, arrows walk the grid, space flips direction, tab/enter
                // jump clues. The on-screen keyboard stays available regardless.
                .onPreviewKeyEvent { event ->
                    if (countingDown || coachVisible || resumeCoachVisible || startLocked) return@onPreviewKeyEvent true
                    if (event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                    when {
                        event.key == Key.Backspace || event.key == Key.Delete -> {
                            engine.deleteLetter()
                            true
                        }
                        event.key == Key.DirectionLeft -> { engine.moveSelection(0, -1); true }
                        event.key == Key.DirectionRight -> { engine.moveSelection(0, 1); true }
                        event.key == Key.DirectionUp -> { engine.moveSelection(-1, 0); true }
                        event.key == Key.DirectionDown -> { engine.moveSelection(1, 0); true }
                        event.key == Key.Spacebar -> { engine.toggleDirection(); true }
                        event.key == Key.Tab || event.key == Key.Enter -> { engine.stepClue(1); true }
                        event.utf16CodePoint.toChar().isLetter() -> {
                            engine.inputLetter(event.utf16CodePoint.toChar())
                            true
                        }
                        else -> false
                    }
                },
            verticalArrangement = Arrangement.spacedBy(Spacing.md),
        ) {
            GameTopBar(
                title = currentPuzzle.title,
                subtitle = if (isDaily) stringResource(Res.string.crossword_daily_badge) else null,
                onBack = leaveSolving,
                trailing = {
                    StatChip(
                        value = formatClock(elapsedMs),
                        tint = colors.gold,
                    )
                    StarBalanceChip(pulseSignal = starPulse)
                    // Cell fill progress (filled / total white squares), e.g. "4/9".
                    // StatChip(
                    //     value = stringResource(Res.string.crossword_progress, filled, totalCells),
                    //     tint = colors.accent,
                    // )
                    if (engine.mistakes > 0) {
                        StatChip(value = engine.mistakes.toString(), tint = colors.danger)
                    }
                    StatChip(
                        value = stringResource(Res.string.crossword_hints_left, hintsLeft),
                        tint = if (hintsExhausted) colors.inkMuted else colors.gold,
                        icon = { tint -> IconBulb(size = 13.dp, tint = tint) },
                    )
                },
            )

            Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
            if (window.isSideBySide) {
                WideLayout(
                    engine = engine,
                    wordCells = wordCells,
                    gutter = gutter,
                    keyboardMode = keyboardMode,
                    dragState = dragState,
                    coachAnchors = coachAnchors,
                    hintsExhausted = hintsExhausted,
                    revealLetterConfig = revealLetterConfig,
                    dailyRevealsRemaining = dailyRevealsRemaining,
                    onLetterRevealed = { dailyRevealsRemaining-- },
                    onStarSpent = { starPulse++ },
                    puzzleId = puzzleId,
                    panelWidth = (window.width * 0.34f).coerceIn(280.dp, 380.dp),
                    showClueList = window.height >= 520.dp,
                    modifier = Modifier.fillMaxSize(),
                )
            } else {
                StackedLayout(
                    engine = engine,
                    wordCells = wordCells,
                    gutter = gutter,
                    keyboardMode = keyboardMode,
                    dragState = dragState,
                    coachAnchors = coachAnchors,
                    hintsExhausted = hintsExhausted,
                    revealLetterConfig = revealLetterConfig,
                    dailyRevealsRemaining = dailyRevealsRemaining,
                    onLetterRevealed = { dailyRevealsRemaining-- },
                    onStarSpent = { starPulse++ },
                    puzzleId = puzzleId,
                    compactKeys = window.isShort,
                    modifier = Modifier.fillMaxSize(),
                )
            }
            }
        }

        // Last in the screen's box, so a letter in mid-air is never clipped by
        // the board frame or hidden behind the rack it is travelling to.
        LetterDragOverlay(
            state = dragState,
            tileSize = if (window.isSideBySide) 36.dp else 46.dp,
        )
        }
    }

    if (leavePrompt) {
        val colors = MaterialTheme.game
        val savedLabel = if (isDaily) stringResource(Res.string.home_daily) else puzzleLabel
        AlertDialog(
            onDismissRequest = { leavePrompt = false },
            containerColor = colors.card,
            shape = RoundedCornerShape(24.dp),
            title = {
                Text(stringResource(Res.string.save_progress_title), color = colors.ink)
            },
            text = {
                Text(stringResource(Res.string.save_progress_body), color = colors.inkMuted)
            },
            confirmButton = {
                Column(modifier = Modifier.fillMaxWidth()) {
                    GameButton(
                        text = stringResource(Res.string.save_progress_save),
                        onClick = {
                            val (filled, total) = engine.fillProgress()
                            AppContainer.analytics.logEvent(
                                AnalyticsEvents.PUZZLE_PROGRESS_SAVED,
                                mapOf(
                                    "puzzle_id" to puzzleId,
                                    "is_daily" to isDaily,
                                    "percent_complete" to if (total > 0) filled * 100 / total else 0,
                                    "elapsed_ms" to elapsedMs,
                                ),
                            )
                            AppContainer.savedSessionManager.save(
                                SavedPuzzleSession(
                                    puzzleId = puzzleId,
                                    puzzleLabel = savedLabel,
                                    isDaily = isDaily,
                                    dailyDateIso = if (isDaily) AppContainer.dailyPuzzleManager.todayIso() else "",
                                    fromChallenge = fromChallenge,
                                    challengerName = challengerName,
                                    challengerTimeMs = challengerTimeMs,
                                    elapsedMs = elapsedMs,
                                    filledCount = filled,
                                    totalCells = total.coerceAtLeast(1),
                                    hintsUsed = engine.hintsUsed,
                                    lettersRevealed = engine.lettersRevealed,
                                    mistakes = engine.mistakes,
                                    selectedRow = engine.selected?.row,
                                    selectedCol = engine.selected?.col,
                                    direction = engine.direction.name,
                                    fills = engine.snapshotFills(),
                                ),
                            )
                            leavePrompt = false
                            onBack()
                        },
                        style = GameButtonStyle.PRIMARY,
                        fillWidth = true,
                    )
                    GameButton(
                        text = stringResource(Res.string.save_progress_discard),
                        onClick = {
                            val (filled, total) = engine.fillProgress()
                            AppContainer.analytics.logEvent(
                                AnalyticsEvents.PUZZLE_PROGRESS_DISCARDED,
                                mapOf(
                                    "puzzle_id" to puzzleId,
                                    "is_daily" to isDaily,
                                    "percent_complete" to if (total > 0) filled * 100 / total else 0,
                                    "elapsed_ms" to elapsedMs,
                                ),
                            )
                            AppContainer.savedSessionManager.clearIfPuzzle(puzzleId)
                            leavePrompt = false
                            onBack()
                        },
                        style = GameButtonStyle.SECONDARY,
                        fillWidth = true,
                        modifier = Modifier.padding(top = Spacing.sm),
                    )
                }
            },
            dismissButton = {},
        )
    }
}

/**
 * Phone/portrait: clue on top, board taking every pixel that's left, controls
 * and keyboard pinned below. The board is weighted rather than fixed so the
 * keyboard never pushes it off screen.
 */
@Composable
private fun StackedLayout(
    engine: CrosswordEngine,
    wordCells: Set<er.prateek.crossly.game.crossword.CellPos>,
    gutter: ClueGutter?,
    keyboardMode: KeyboardMode,
    dragState: LetterDragState,
    coachAnchors: CoachMarkAnchors,
    hintsExhausted: Boolean,
    revealLetterConfig: RevealLetterConfig,
    dailyRevealsRemaining: Int,
    onLetterRevealed: () -> Unit,
    onStarSpent: () -> Unit,
    puzzleId: String,
    compactKeys: Boolean,
    modifier: Modifier = Modifier,
) {
    BoxWithConstraints(modifier = modifier.fillMaxWidth()) {
        // The keyboard is sized from what's actually left rather than a fixed
        // constant: on a short window a fixed keyboard eats the entire column and
        // the board collapses to a sliver. Keys give up height first, the board
        // keeps what remains, and both stay on screen.
        val keyHeight = (maxHeight * 0.085f).coerceIn(30.dp, 46.dp)
        // Bumped every time a gutter clue is tapped, so the top clue bar can flash
        // even when the tapped clue was already active -- proof to the player that
        // what they just tapped is the full text shown above.
        var clueBarPulse by remember(engine) { mutableStateOf(0) }

        Column(
            modifier = Modifier.fillMaxSize(),
            verticalArrangement = Arrangement.spacedBy(if (compactKeys) Spacing.sm else Spacing.md),
        ) {
            ClueBar(
                clue = engine.currentClue(),
                direction = engine.direction,
                onPrevious = { engine.stepClue(-1) },
                onNext = { engine.stepClue(1) },
                pulseSignal = clueBarPulse,
                modifier = Modifier.reportCoachBounds { coachAnchors.clue = it },
            )
            CrosswordGridView(
                engine = engine,
                highlightedCells = wordCells,
                gutter = gutter,
                activeClue = engine.currentClue(),
                activeDirection = engine.direction,
                onSelectClue = { clue, direction -> engine.selectClue(clue, direction); clueBarPulse++ },
                dragState = dragState,
                minCellSize = 18.dp,
                modifier = Modifier.weight(1f).fillMaxWidth().reportCoachBounds { coachAnchors.board = it },
            )
            ActionBar(
                engine = engine,
                hintsExhausted = hintsExhausted,
                revealLetterConfig = revealLetterConfig,
                dailyRevealsRemaining = dailyRevealsRemaining,
                onLetterRevealed = onLetterRevealed,
                onStarSpent = onStarSpent,
                puzzleId = puzzleId,
            )
            LetterInput(
                engine = engine,
                mode = keyboardMode,
                wordCells = wordCells,
                dragState = dragState,
                keyHeight = keyHeight,
                modifier = Modifier.reportCoachBounds { coachAnchors.letters = it },
            )
        }
    }
}

/**
 * Desktop/tablet-landscape: board on the left at whatever size fits, a fixed
 * side panel on the right with the clue, the full Across/Down lists and the
 * controls. This is the layout the wide breakpoint exists for.
 */
@Composable
private fun WideLayout(
    engine: CrosswordEngine,
    wordCells: Set<er.prateek.crossly.game.crossword.CellPos>,
    gutter: ClueGutter?,
    keyboardMode: KeyboardMode,
    dragState: LetterDragState,
    coachAnchors: CoachMarkAnchors,
    hintsExhausted: Boolean,
    revealLetterConfig: RevealLetterConfig,
    dailyRevealsRemaining: Int,
    onLetterRevealed: () -> Unit,
    onStarSpent: () -> Unit,
    puzzleId: String,
    panelWidth: Dp,
    showClueList: Boolean,
    modifier: Modifier = Modifier,
) {
    // Bumped every time a gutter clue is tapped, so the top clue bar can flash
    // even when the tapped clue was already active -- proof to the player that
    // what they just tapped is the full text shown above.
    var clueBarPulse by remember(engine) { mutableStateOf(0) }

    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(Spacing.xl),
    ) {
        Box(
            modifier = Modifier
                .weight(1f)
                .fillMaxHeight()
                .reportCoachBounds { coachAnchors.board = it },
            contentAlignment = Alignment.Center,
        ) {
            CrosswordGridView(
                engine = engine,
                highlightedCells = wordCells,
                gutter = gutter,
                activeClue = engine.currentClue(),
                activeDirection = engine.direction,
                onSelectClue = { clue, direction -> engine.selectClue(clue, direction); clueBarPulse++ },
                dragState = dragState,
                maxCellSize = 92.dp,
                modifier = Modifier.fillMaxSize(),
            )
        }
        Column(
            modifier = Modifier.width(panelWidth).fillMaxHeight(),
            verticalArrangement = Arrangement.spacedBy(Spacing.md),
        ) {
            ClueBar(
                clue = engine.currentClue(),
                direction = engine.direction,
                onPrevious = { engine.stepClue(-1) },
                onNext = { engine.stepClue(1) },
                pulseSignal = clueBarPulse,
                modifier = Modifier.reportCoachBounds { coachAnchors.clue = it },
            )
            if (showClueList) {
                ClueList(engine = engine, modifier = Modifier.weight(1f).fillMaxWidth())
            }
            ActionBar(
                engine = engine,
                hintsExhausted = hintsExhausted,
                revealLetterConfig = revealLetterConfig,
                dailyRevealsRemaining = dailyRevealsRemaining,
                onLetterRevealed = onLetterRevealed,
                onStarSpent = onStarSpent,
                puzzleId = puzzleId,
            )
            LetterInput(
                engine = engine,
                mode = keyboardMode,
                wordCells = wordCells,
                dragState = dragState,
                keyHeight = 34.dp,
                modifier = Modifier.reportCoachBounds { coachAnchors.letters = it },
            )
        }
    }
}

/**
 * The letter source at the bottom of both layouts.
 *
 * Which keys appear is the player's choice (Settings > How you play): the whole
 * alphabet, only the letters still missing from the grid, or just the tiles for
 * the word under the cursor. A physical keyboard keeps working in every mode —
 * the rack narrows what's offered, not what's accepted.
 */
@Composable
private fun LetterInput(
    engine: CrosswordEngine,
    mode: KeyboardMode,
    wordCells: Set<er.prateek.crossly.game.crossword.CellPos>,
    dragState: LetterDragState,
    keyHeight: Dp,
    modifier: Modifier = Modifier,
) {
    val onLetter: (Char) -> Unit = { engine.inputLetter(it) }
    val onDelete: () -> Unit = { engine.deleteLetter() }
    // A dropped letter goes where it was let go, not where the cursor happened
    // to be.
    val onDropOnCell: (er.prateek.crossly.game.crossword.CellPos, Char) -> Unit = { pos, letter ->
        engine.placeLetterAt(pos, letter)
    }
    val clue = engine.currentClue()

    when {
        mode == KeyboardMode.FULL -> KeyboardBar(
            onLetter = onLetter,
            onDelete = onDelete,
            keyHeight = keyHeight,
            dragState = dragState,
            onDropOnCell = onDropOnCell,
            modifier = modifier,
        )

        // Word mode with no cursor yet has nothing to build a rack from; the
        // grid-wide letters are the sensible stand-in until a square is picked.
        mode == KeyboardMode.WORD && clue != null -> {
            val rack = remember(clue.answer) { LetterSupply.wordRack(clue.answer) }
            LetterRackBar(
                tiles = LetterSupply.tilesFor(rack, LetterSupply.placedLettersInCurrentWord(engine, wordCells)),
                onLetter = onLetter,
                onDelete = onDelete,
                keyHeight = keyHeight,
                dragState = dragState,
                onDropOnCell = onDropOnCell,
                modifier = modifier,
            )
        }

        // Never marked spent -- the whole point of this mode is that a letter
        // doesn't disappear from the rack once its occurrences are filled in.
        mode == KeyboardMode.THIS_PUZZLE -> LetterRackBar(
            tiles = LetterSupply.allPuzzleLetters(engine).map { RackTile(it, spent = false) },
            onLetter = onLetter,
            onDelete = onDelete,
            keyHeight = keyHeight,
            dragState = dragState,
            onDropOnCell = onDropOnCell,
            modifier = modifier,
        )

        else -> LetterRackBar(
            tiles = LetterSupply.relevantLetters(engine).map { RackTile(it, spent = false) },
            onLetter = onLetter,
            onDelete = onDelete,
            keyHeight = keyHeight,
            dragState = dragState,
            onDropOnCell = onDropOnCell,
            modifier = modifier,
        )
    }
}

@Composable
private fun ActionBar(
    engine: CrosswordEngine,
    hintsExhausted: Boolean,
    revealLetterConfig: RevealLetterConfig,
    dailyRevealsRemaining: Int,
    onLetterRevealed: () -> Unit,
    onStarSpent: () -> Unit,
    puzzleId: String,
) {
    val colors = MaterialTheme.game
    val scope = rememberCoroutineScope()
    var revealLoading by remember(engine) { mutableStateOf(false) }
    var noStarsMessage by remember(engine) { mutableStateOf(false) }

    // Per-puzzle half of the cap lives on the engine (resets with the attempt); the daily
    // half is passed in from the screen (persists across puzzles). Whichever is tighter wins.
    // `0` means unlimited, matching every other cap in AppConfig (see AdsConfig).
    val perPuzzleMax = revealLetterConfig.maxPerPuzzle(engine.puzzle.difficulty)
    val perPuzzleRemaining = if (perPuzzleMax <= 0) {
        Int.MAX_VALUE
    } else {
        (perPuzzleMax - engine.lettersRevealed).coerceAtLeast(0)
    }
    val revealsRemaining = minOf(perPuzzleRemaining, dailyRevealsRemaining)
    val revealLetterExhausted = revealsRemaining <= 0
    // Only demand an ad when one can actually be offered -- ad-free entitlements, a disabled
    // rewarded config or a platform with no rewarded support all fall through to a free reveal.
    val adRequired = revealLetterConfig.requireAd && AppContainer.adsCoordinator.isRewardedAvailable()

    // Auto-dismisses so the message reads as a transient toast rather than a
    // permanent fixture that pushes the layout around.
    LaunchedEffect(noStarsMessage) {
        if (noStarsMessage) {
            delay(2400)
            noStarsMessage = false
        }
    }

    fun grantReveal() {
        // Charged only once the reveal is actually granted -- an ad watched with
        // no stars left still costs nothing extra, it just doesn't pay off.
        if (!AppContainer.progressionManager.spendStar(revealLetterConfig.starCost)) {
            noStarsMessage = true
            AppContainer.analytics.logEvent(
                AnalyticsEvents.REVEAL_BLOCKED_NO_STARS,
                mapOf("puzzle_id" to puzzleId, "star_cost" to revealLetterConfig.starCost),
            )
            return
        }
        onStarSpent()
        engine.revealSelectedCell()
        AppContainer.revealLimiter.recordRevealUsed()
        onLetterRevealed()
        AppContainer.analytics.logEvent(
            AnalyticsEvents.REVEAL_LETTER_USED,
            mapOf(
                "puzzle_id" to puzzleId,
                "star_cost" to revealLetterConfig.starCost,
                "stars_remaining" to AppContainer.progressionManager.starsAvailable(),
                "letters_revealed_total" to engine.lettersRevealed,
                "revealed_via_ad" to adRequired,
                "revealsRemainingAfter" to (revealsRemaining - 1).coerceAtLeast(0),
            ),
        )
    }

    val onRevealLetter: () -> Unit = onRevealLetter@{
        if (revealLetterExhausted || revealLoading) return@onRevealLetter
        noStarsMessage = false
        if (!adRequired) {
            grantReveal()
            return@onRevealLetter
        }
        AppContainer.analytics.logEvent(
            AnalyticsEvents.AD_REWARDED_REQUESTED,
            mapOf("placement" to AdPlacement.CROSSWORD.wireName, "puzzle_id" to puzzleId),
        )
        revealLoading = true
        scope.launch {
            val earned = AppContainer.adsCoordinator.showRewarded(AdPlacement.CROSSWORD)
            revealLoading = false
            if (earned) grantReveal()
        }
    }

    Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(Spacing.xs)) {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
    // Below this width "Reveal Letter" can't fit on one line and Compose breaks
    // it mid-word; the icons already carry the distinction, so the labels shorten.
    val useShortLabels = maxWidth < 300.dp
    Row(
        modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min),
        horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
    ) {
        GameButton(
            text = stringResource(Res.string.crossword_check),
            onClick = {
                engine.checkGrid()
                val (filledNow, totalNow) = engine.fillProgress()
                AppContainer.analytics.logEvent(
                    AnalyticsEvents.GRID_CHECKED,
                    mapOf(
                        "puzzle_id" to puzzleId,
                        "all_correct" to engine.cells.none { it.status == CellStatus.INCORRECT },
                        "filled_cells" to filledNow,
                        "total_cells" to totalNow,
                        "mistakes_total" to engine.mistakes,
                    ),
                )
            },
            style = GameButtonStyle.SECONDARY,
            size = GameButtonSize.SMALL,
            fillWidth = true,
            fillHeight = true,
            modifier = Modifier.weight(1f),
            icon = if (useShortLabels) null else { { tint -> IconCheck(size = 16.dp, tint = tint) } },
        )
        GameButton(
            text = if (revealLoading) {
                stringResource(Res.string.crossword_reveal_ad_loading)
            } else {
                stringResource(
                    if (useShortLabels) Res.string.crossword_reveal_cell_count_short else Res.string.crossword_reveal_cell_count,
                    // Both caps set to "unlimited" (0) is a real, if unusual, config -- cap the
                    // display rather than print Int.MAX_VALUE at the player.
                    revealsRemaining.coerceAtMost(99),
                )
            },
            caption = if (adRequired && !revealLoading) stringResource(Res.string.crossword_reveal_ad_caption) else null,
            onClick = onRevealLetter,
            style = GameButtonStyle.SECONDARY,
            size = GameButtonSize.SMALL,
            enabled = !revealLetterExhausted && !revealLoading,
            fillWidth = true,
            fillHeight = true,
            modifier = Modifier.weight(1f),
            icon = if (useShortLabels) null else { { tint -> IconBulb(size = 16.dp, tint = tint) } },
        )
        // A much bigger assist than a single letter -- hidden unless an operator
        // has deliberately turned it on in Remote Config (off by default).
        if (revealLetterConfig.showRevealPuzzleButton) {
            GameButton(
                text = stringResource(
                    if (useShortLabels) Res.string.crossword_reveal_grid_short else Res.string.crossword_reveal_grid,
                ),
                onClick = {
                    engine.revealGrid()
                    AppContainer.analytics.logEvent(
                        AnalyticsEvents.REVEAL_GRID_USED,
                        mapOf("puzzle_id" to puzzleId, "hints_used_total" to engine.hintsUsed),
                    )
                },
                style = GameButtonStyle.SECONDARY,
                size = GameButtonSize.SMALL,
                enabled = !hintsExhausted,
                fillWidth = true,
                fillHeight = true,
                modifier = Modifier.weight(1f),
                icon = if (useShortLabels) null else { { tint -> IconEye(size = 16.dp, tint = tint) } },
            )
        }
    }
    }
    if (noStarsMessage) {
        Text(
            text = stringResource(Res.string.crossword_no_stars),
            style = MaterialTheme.typography.labelMedium,
            color = colors.danger,
        )
    }
    }
}

/**
 * The player's spendable star balance (earned stars minus stars spent on letter reveals) --
 * pinned in the top bar so it's always visible while playing. [pulseSignal] bumps on every
 * successful spend, driving a quick scale/color flash on top of the count itself changing, so
 * "you just spent a star" reads as an event rather than a number quietly ticking down.
 */
@Composable
private fun StarBalanceChip(pulseSignal: Int) {
    val colors = MaterialTheme.game
    val stars = AppContainer.progressionManager.starsAvailable()

    val scale = remember { Animatable(1f) }
    var flashing by remember { mutableStateOf(false) }
    LaunchedEffect(pulseSignal) {
        if (pulseSignal > 0) {
            flashing = true
            scale.snapTo(1.35f)
            scale.animateTo(1f, spring(dampingRatio = Spring.DampingRatioMediumBouncy))
            delay(350)
            flashing = false
        }
    }
    val flashTint by animateColorAsState(
        if (flashing) colors.danger else colors.gold,
        tween(400),
        label = "starFlash",
    )

    StatChip(
        value = stars.toString(),
        icon = { tint -> IconStar(size = 14.dp, tint = tint) },
        tint = flashTint,
        modifier = Modifier.graphicsLayer { scaleX = scale.value; scaleY = scale.value },
    )
}
